import React, { useState, useRef, useEffect } from 'react';
import { useAppStore } from '@/hooks/useAppStore';
import { Language } from '@/types';
import { cn } from '@/lib/utils';
import JAIcon from '../icons/languages/ja';
import ZHIcon from '../icons/languages/zh';
import EnIcon from '../icons/languages/en';
import { useTranslation } from '@/lib/translate';
import { GripVertical, Check } from 'lucide-react';

// Re-export supportedLanguages for useAppStore
export const supportedLanguages: Array<{ code: Language; name: string }> = [
  { 
    code: 'en', 
    name: 'English',
  },
  { 
    code: 'ja', 
    name: 'Japanese',
  },
  { 
    code: 'zh', 
    name: 'Chinese',
  },
];

const LanguageIcon: React.FC<{ lang: Language; size?: number }> = ({ lang, size = 24 }) => {
  const props = { width: size, height: size };
  
  if (lang === 'ja') {
    return <JAIcon {...props} className='rounded-sm' />;
  } else if (lang === 'zh') {
    return <ZHIcon {...props} className='rounded-sm' />;
  } else if (lang === 'en') {
    return <EnIcon {...props} className='rounded-sm' />;
  }
  return null;
};

export const LanguageSelector: React.FC = () => {
  const targetLanguages = useAppStore((state) => state.targetLanguages);
  const setTargetLanguages = useAppStore((state) => state.setTargetLanguages);
  const preferredLanguage = useAppStore((state) => state.preferredLanguage);
  const t = useTranslation(preferredLanguage);
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLanguageToggle = (langCode: Language) => {
    if (targetLanguages.includes(langCode)) {
      // Prevent removing the last language
      if (targetLanguages.length > 1) {
        setTargetLanguages(targetLanguages.filter(l => l !== langCode));
      }
    } else {
      setTargetLanguages([...targetLanguages, langCode]);
    }
  };

  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [isTouchDrag, setIsTouchDrag] = useState(false);

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    dragItem.current = index;
    setIsTouchDrag(false);
    // Delay setting the state so the browser captures the full element as the drag image
    // before we turn it into a ghost.
    setTimeout(() => {
        setDraggedIndex(index);
    }, 0);
    e.dataTransfer.effectAllowed = "move";
  };

  const reorderItems = (dragIndex: number, hoverIndex: number) => {
    if (dragIndex === hoverIndex) return;

    const newLanguages = [...targetLanguages];
    const draggedItemContent = newLanguages[dragIndex];
    
    // Remove from old pos
    newLanguages.splice(dragIndex, 1);
    // Insert at new pos
    newLanguages.splice(hoverIndex, 0, draggedItemContent);

    // Update refs to track the new position of the dragged item
    dragItem.current = hoverIndex;
    setDraggedIndex(hoverIndex);
    
    // Update state immediately
    setTargetLanguages(newLanguages);
  };

  const handleDragEnter = (_: React.DragEvent<HTMLDivElement>, index: number) => {
    if (dragItem.current === null) return;
    reorderItems(dragItem.current, index);
  };

  const handleDragEnd = (_: React.DragEvent<HTMLDivElement>) => {
    dragItem.current = null;
    dragOverItem.current = null;
    setDraggedIndex(null);
    setIsTouchDrag(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault(); // Necessary to allow dropping and drag events
  };

  // Touch Handlers for Mobile DnD
  const handleTouchStart = (_: React.TouchEvent<HTMLDivElement>, index: number) => {
    dragItem.current = index;
    setDraggedIndex(index);
    setIsTouchDrag(true);
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (dragItem.current === null) return;
    
    const touch = e.touches[0];
    const target = document.elementFromPoint(touch.clientX, touch.clientY);
    const closestItem = target?.closest('[data-language-item="true"]');
    
    if (closestItem) {
        const indexStr = closestItem.getAttribute('data-index');
        if (indexStr) {
            const index = parseInt(indexStr, 10);
            if (!isNaN(index) && index !== dragItem.current) {
                reorderItems(dragItem.current, index);
            }
        }
    }
  };

  const handleTouchEnd = () => {
    dragItem.current = null;
    setDraggedIndex(null);
    setIsTouchDrag(false);
  };

  const firstSelectedLang = targetLanguages.length > 0 ? targetLanguages[0] : 'en';

  const selectedLanguagesList = targetLanguages.map(code => 
    supportedLanguages.find(l => l.code === code)
  ).filter((l): l is typeof supportedLanguages[0] => !!l);

  const availableLanguagesList = supportedLanguages.filter(l => !targetLanguages.includes(l.code));

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className={cn(
          "h-10 border-2 border-black/25 bg-white/30 backdrop-blur-md rounded-lg flex items-center cursor-pointer transition-all duration-200 ease-in-out hover:bg-white/50 hover:border-black/40 hover:scale-105 active:scale-100 w-10 min-w-auto px-0 justify-center gap-0"
        )}
      >
        <div className="relative">
           <LanguageIcon lang={firstSelectedLang} size={24} />
           {targetLanguages.length > 1 && (
             <div className="absolute -top-1 -right-1 bg-red-500 text-white text-[9px] w-3.5 h-3.5 flex items-center justify-center rounded-full font-bold border border-white">
               {targetLanguages.length}
             </div>
           )}
        </div>
      </button>

      {open && (
        <div
          className="absolute bottom-full right-0 mb-2 bg-white/95 backdrop-blur-xl rounded-xl p-2 shadow-xl border border-black/10 z-[1000] min-w-[200px]"
        >
          {/* Selected Languages Header */}
          <div className="px-3 py-2 text-xs font-semibold text-[#666] border-b border-black/10 mb-1 text-center whitespace-nowrap">
            {t('Selected Languages')}
          </div>

          {/* Selected Languages List */}
          {selectedLanguagesList.map((lang, index) => {
            const isDragging = index === draggedIndex;
            return (
            <div
              key={lang.code}
              data-language-item="true"
              data-index={index}
              className={cn(
                "flex items-center gap-2 px-2 py-2 border-2 border-transparent rounded-lg text-sm font-medium text-[#333] w-full transition-all duration-200 ease-in-out whitespace-nowrap mb-1 font-semibold cursor-pointer touch-none",
                isDragging && !isTouchDrag ? "opacity-40 border-dashed border-gray-300 bg-transparent [&>*]:opacity-0" : "",
                isDragging && isTouchDrag ? "bg-white shadow-lg scale-105 z-10 border-chotto-blue" : "bg-[rgba(77,208,225,0.1)] hover:bg-[rgba(77,208,225,0.2)]"
              )}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnter={(e) => handleDragEnter(e, index)}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
              onTouchStart={(e) => handleTouchStart(e, index)}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onClick={() => handleLanguageToggle(lang.code)}
            >
              {/* Uncheck (Remove) Button - Visual Only */}
              <div 
                className="w-5 h-5 rounded border border-chotto-blue bg-chotto-blue flex items-center justify-center hover:bg-chotto-blue/80 flex-shrink-0"
              >
                <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
              </div>

              <LanguageIcon lang={lang.code} size={20} />
              <span className="flex-grow select-none">{t(lang.name)}</span>

              {/* Grip Handle - Stop propagation to prevent toggle when just clicking handle */}
              <div 
                className="cursor-move text-gray-400 hover:text-gray-600 p-0.5"
                title={t("Drag to reorder")}
                onClick={(e) => e.stopPropagation()}
              >
                <GripVertical size={16} />
              </div>
            </div>
          )})}

          {/* Available Languages Header (only show if there are available languages) */}
          {availableLanguagesList.length > 0 && (
            <>
              <div className="px-3 py-2 text-xs font-semibold text-[#666] border-b border-black/10 mb-1 mt-2 text-center whitespace-nowrap">
                {t('Available Languages')}
              </div>
              
              {availableLanguagesList.map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageToggle(lang.code)}
                  className="flex items-center gap-2 px-3 py-2 border-none rounded-lg cursor-pointer text-sm font-medium text-[#333] w-full text-left transition-all duration-200 ease-in-out whitespace-nowrap hover:bg-black/5 bg-transparent"
                >
                  <div className="w-5 h-5 rounded border border-gray-400 bg-white flex items-center justify-center flex-shrink-0" />
                  <LanguageIcon lang={lang.code} size={20} />
                  <span>{t(lang.name)}</span>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};
